import React, {useCallback, useState} from 'react';
import _ from 'lodash';
import {Box, Container, Divider, IconButton, Skeleton, Stack, Typography} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {useSnackbar} from 'notistack';
import {useQueryClient} from '@tanstack/react-query';
import SCTextField from '../components/SCTextField.js';
import withMainContainer from '../components/WithMainContainer.js';
import {withTitle} from '../title.js';
import {useUser} from '../util/user.js';
import {useBusyCallback, useOnce} from '../util/react_hooks.js';
import {API_ERRORS_HANDLED, toastApiErrors} from '../util/api_util.js';
import api from '../util/api.js';
import SCButton from '../components/SCButton.js';

const UsernameUpdate = ()=>{
    let [edit, setEdit] = useState();
    let [user, userLoading] = useUser();
    let [name, setName] = useState('');
    let queryClient = useQueryClient();
    let {enqueueSnackbar} = useSnackbar();
    let update = useCallback(async ()=>{
        queryClient.setQueryData(['user'], data=>{
            let updated = _.cloneDeep(data);
            updated.user.username = name;
            return updated;
        });
        setEdit(false);
        let res = await toastApiErrors(api.updateUsername(name), enqueueSnackbar);
        queryClient.invalidateQueries({queryKey: ['user']});
        if (res==API_ERRORS_HANDLED)
            return;
        enqueueSnackbar(`שם המשתמש עודכן בהצלחה`, {variant: 'success'});
    }, [name, queryClient, enqueueSnackbar]);
    useOnce(()=>setName(user.username), !!user);
    return <Stack gap={2}>
      <Typography variant="h5">שם משתמש</Typography>
      {userLoading ? <Skeleton
        width={160}
        height={30}
        variant="rounded"
      /> : edit ? <Stack direction="row" gap={1} alignItems="center">
        <Box maxWidth={250}>
          <SCTextField size="small" value={name} onChange={setName}/>
        </Box>
        <IconButton size="small" onClick={update} color="primary">
          <CheckIcon/>
        </IconButton>
        <IconButton size="small" onClick={()=>setEdit(false)}>
          <CloseIcon/>
        </IconButton>
      </Stack> : <Stack direction="row" gap={1} alignItems="center">
        <Typography fontWeight="bold">{user?.username}</Typography>
        {<IconButton size="small" onClick={()=>setEdit(true)}>
          <EditIcon/>
        </IconButton>}
      </Stack>}
    </Stack>;
};

const PasswordUpdate = ()=>{
    let [password, setPassword] = useState('');
    let [newPassword, setNewPassword] = useState('');
    let [repeatPassword, setRepeatPassword] = useState('');
    let {enqueueSnackbar} = useSnackbar();
    let [update, updating] = useBusyCallback(async ()=>{
        let res = await toastApiErrors(
            api.updatePassword({password, newPassword, repeatPassword}), enqueueSnackbar);
        if (res==API_ERRORS_HANDLED)
            return;
        enqueueSnackbar(`הסיסמה עודכנה בהצלחה`, {variant: 'success'});
        setPassword('');
        setNewPassword('');
        setRepeatPassword('');
    }, [password, newPassword, repeatPassword, enqueueSnackbar]);
    return <Stack gap={2}>
      <Typography variant="h5">עדכון סיסמה</Typography>
      <Stack gap={1} maxWidth={400}>
        <SCTextField type="password" label="סיסמה נוכחית"
          value={password} onChange={setPassword}/>
        <SCTextField type="password" label="סיסמה חדשה"
          value={newPassword} onChange={setNewPassword}/>
        <SCTextField type="password" label="אימות סיסמה חדשה"
          value={repeatPassword} onChange={setRepeatPassword}/>
        <SCButton text="עדכון סיסמה" variant="outlined"
          onClick={update} loading={updating}/>
      </Stack>
    </Stack>;
};

const UserSettings = ()=>{
    let [user, userLoading] = useUser();
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">פרטים אישיים</Typography>
        <Typography variant="h5">דוא"ל</Typography>
        {userLoading ? <Skeleton width={200} height={30} variant="rounded"/>
            : <Typography fontWeight="bold">{user?.email}</Typography>}
        <Divider/>
        <UsernameUpdate/>
        <Divider/>
        <PasswordUpdate/>
      </Stack>
    </Container>;
};

export default withTitle('עדכון פרטים אישיים', withMainContainer(UserSettings));